<script>
	import { onMount } from "svelte";
	import { Link } from "svelte-routing";
	import { currentUser } from "../store/store.js";
	import { User } from "../utils/login.js";
	import { fetchBrfs } from "../utils/search.js";
	import Header from "../components/Header.svelte";
	import Search from "../components/Search.svelte";
	import UploadFile from "../components/UploadFile.svelte";
	import PropertyMap from "../components/PropertyMap.svelte";
	import TopBrfCard from "../components/TopBrfCard.svelte";
	import { selectedItem } from "../store/store.js";
	import FAQ from "../components/FAQ.svelte";
	import Partners from "../components/Partners.svelte";

	let headlines = [
		"Styr föreningens ekonomi med insikt",
		"Allt du behöver veta före bostadsbeslutet",
		"Jämför BRF:er",
	];
	let currentHeadlineIndex = 0;
	let headlineState = "in"; // 'in' or 'out'

	onMount(async () => {
		window.scrollTo(0, 0);
		fetchBrfs();
		await checkAuthStatus();
		getUserLocation();

		const interval = setInterval(() => {
			headlineState = "out";
		}, 6000); // Time the headline is visible

		return () => {
			clearInterval(interval);
		};
	});

	function handleAnimationEnd() {
		if (headlineState === "out") {
			currentHeadlineIndex =
				(currentHeadlineIndex + 1) % headlines.length;
			headlineState = "in";
		}
	}

	function getUserLocation() {
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const { latitude, longitude } = position.coords;
					// Set a mock selectedItem for the map to use the user's location
					selectedItem.set({
						latitude: latitude,
						longitude: longitude,
						name: "Din plats",
						core_id: "user-location", // A unique ID to prevent it from being filtered as a nearby BRF
					});
				},
				(error) => {
					console.error("Error getting user location:", error);
					// Fallback to a default location if user denies permission
					selectedItem.set({
						latitude: 59.323807,
						longitude: 18.071437,
						name: "Stockholm",
						core_id: "default-location",
					});
				},
			);
		} else {
			console.error("Geolocation is not supported by this browser.");
			// Fallback to a default location if geolocation is not supported
			selectedItem.set({
				latitude: 59.323807,
				longitude: 18.071437,
				name: "Stockholm",
				core_id: "default-location",
			});
		}
	}

	async function checkAuthStatus() {
		try {
			const storedUser = localStorage.getItem("currentUser");
			if (storedUser) {
				try {
					const userData = JSON.parse(storedUser);
					currentUser.set(new User(userData));
					return true;
				} catch (e) {
					console.error("Error parsing stored user data:", e);
					localStorage.removeItem("currentUser"); // Clear invalid data
				}
			}

			// Check if we have a token in cookies before making the request
			// This prevents the 401 error when no authentication exists
			const hasAuthCookie = document.cookie
				.split(";")
				.some(
					(cookie) =>
						cookie.trim().startsWith("auth=") ||
						cookie.trim().startsWith("session="),
				);

			if (!hasAuthCookie) {
				// No auth cookie found, skip the server request
				return false;
			}

			// Only try server authentication if we might have a valid session
			try {
				const response = await fetch(
					`https://boanalys-backend-324523837899.europe-north1.run.app/users/me`,
					{
						credentials: "include",
						headers: {
							Accept: "application/json",
						},
					},
				);

				if (response.ok) {
					const userData = await response.json();

					// Create user object
					const userObj = new User({
						accessToken: userData.access_token,
						type: userData.type,
						numberOfAnalysis: userData.number_of_analysis,
						username: userData.username,
					});

					// Store in localStorage for future use
					localStorage.setItem(
						"currentUser",
						JSON.stringify(userObj),
					);

					// Set user in store
					currentUser.set(userObj);

					return true;
				} else if (response.status === 401) {
					// User is not authenticated
					currentUser.set(null);
					localStorage.removeItem("currentUser"); // Clear any stale data
					return false;
				} else {
					// Other error
					console.error(
						`Authentication check failed: ${response.status} ${response.statusText}`,
					);
					return false;
				}
			} catch (error) {
				console.error("Error checking authentication status:", error);
				return false;
			}
		} catch (error) {
			console.error("Error checking authentication status:", error);
			return false;
		}
	}
</script>

<svelte:head>
	<title>Boanalys - AI-analys av din bostadsrättsförening</title>
	<meta
		name="description"
		content="Boanalys använder artificiell intelligens för att analysera bostadsrättsföreningar. Få djupgående insikter om ekonomi, drift och underhåll för smartare bostadsbeslut."
	/>
	<link rel="canonical" href="https://boanalys.io/" />
</svelte:head>

<main>
	<Header />

	<section class="hero">
		<div class="animated-background">
			<div class="shape shape-1"></div>
			<div class="shape shape-2"></div>
			<div class="shape shape-3"></div>
			<div class="shape shape-4"></div>
		</div>
		<div class="hero-content">
			<h1
				class="animate__animated"
				class:animate__fadeInLeft={headlineState === "in"}
				class:animate__backOutRight={headlineState === "out"}
				on:animationend={handleAnimationEnd}
			>
				{headlines[currentHeadlineIndex]}
			</h1>
			<p class="sub-headline">
				Smidig överblick. Djupa insikter. Bättre beslut.
			</p>
			<div class="trust-badges">
				<span class="trust-badge">✓ 30 000+ BRF:er</span>
				<!-- <span class="trust-badge">✓ Alltid gratis</span> -->
			</div>
			<Search />
		</div>
	</section>

	<section class="benefits-section">
		<h2>Vad får du reda på?</h2>
		<div class="benefits">
			<div class="benefit">
				<i class="fas fa-chart-line fa-3x"></i>
				<h3>Upptäck om avgiften kommer höjas</h3>
				<p>
					Analysera ekonomiska trender och få varningar om kommande
					avgiftshöjningar.
				</p>
			</div>
			<div class="benefit">
				<i class="fas fa-tools fa-3x"></i>
				<h3>Se dolda renoveringsbehov i föreningen</h3>
				<p>
					Identifiera kommande underhållskostnader som kan påverka din
					ekonomi.
				</p>
			</div>
			<div class="benefit">
				<i class="fas fa-star fa-3x"></i>
				<h3>Få ett oberoende betyg på föreningen</h3>
				<p>
					Vår AI ger ett tydligt betyg på föreningens ekonomiska
					hälsa.
				</p>
			</div>
			<div class="benefit">
				<i class="fas fa-robot fa-3x"></i>
				<h3>Få svar på dina frågor direkt</h3>
				<p>
					BoBot analyserar alla dokument och ger dig konkreta svar på
					direkten.
				</p>
			</div>
		</div>
	</section>

	<section class="how-it-works">
		<h2>Så enkelt är det</h2>
		<div class="steps">
			<div class="step">
				<div class="step-icon"><i class="fas fa-search"></i></div>
				<h3>1. Sök förening</h3>
				<p>
					Skriv föreningens namn eller adress - vi har redan
					analyserat 30 000+ föreningar.
				</p>
			</div>
			<div class="step">
				<div class="step-icon"><i class="fas fa-chart-bar"></i></div>
				<h3>2. Få din analys på direkten</h3>
				<p>
					Se betyg, ekonomiska nyckeltal, stadgar, energideklarationer
					och mycket mer.
				</p>
			</div>
			<div class="step">
				<div class="step-icon"><i class="fas fa-comments"></i></div>
				<h3>3. Ställ frågor till BoBot</h3>
				<p>
					Få konkreta svar om allt från avgiftsutveckling till
					kommande renoveringar.
				</p>
			</div>
		</div>
	</section>

	<section class="about-map-section">
		<div class="about-map-content">
			<div class="about-container">
				<h2 style="margin-top: 0;">
					Vi bygger framtidens BRF-plattform.
				</h2>
				<p>
					Är du nyfiken på hur en fullständig analys från Boanalys ser
					ut? Klicka på knappen nedan för att se ett exempel på en
					analys för en bostadsrättsförening och upptäck de insikter
					du kan få om ekonomi, stadgar och mycket mer.
				</p>
				<Link to="/brf/27869/bf-heimdal-3">
					<button class="action-button">Se exempelanalys</button>
				</Link>
			</div>
			<div class="map-container">
				<PropertyMap height="520px" />
			</div>
		</div>
	</section>

	<section class="upload-section">
		<div class="upload-content">
			<h2>Hittar du inte din förening?</h2>
			{#if $currentUser}
				<p>
					Hjälp oss att göra Boanalys ännu bättre. Ladda upp
					föreningens årsredovisning så analyserar vi den direkt.
				</p>
				<UploadFile />
			{:else}
				<p>
					Ladda upp en årsredovisning, spara analyser och få tillgång
					till fler funktioner. Skapa ett gratiskonto eller logga in
					för att fortsätta.
				</p>
				<div class="button-group">
					<Link to="/login?view=signup">
						<button class="action-button">Skapa konto gratis</button
						>
					</Link>
					<Link to="/login">
						<button class="action-button">Logga in</button>
					</Link>
				</div>
			{/if}
		</div>
	</section>

	<section class="toplist-section">
		<div class="toplist-container">
			<TopBrfCard />
		</div>
	</section>

	<FAQ />
	<Partners />

	<section class="b2b-section">
		<div class="b2b-content">
			<div class="b2b-badge">För banker & finansiella aktörer</div>
			<h2>Effektivisera er kreditbedömning av BRF:er</h2>
			<p>
				Boanalys erbjuder banker ett komplett verktyg för AI-driven
				riskbedömning av bostadsrättsföreningar – med automatisk analys,
				portföljöverblick och API-integration.
			</p>
			<div class="b2b-features">
				<div class="b2b-feature">
					<i class="fas fa-database"></i>
					<span>100+ nyckeltal direkt från källan</span>
				</div>
				<div class="b2b-feature">
					<i class="fas fa-layer-group"></i>
					<span>Portföljövervakning & bulk-analys</span>
				</div>
				<div class="b2b-feature">
					<i class="fas fa-code"></i>
					<span>REST API-integration</span>
				</div>
				<div class="b2b-feature">
					<i class="fas fa-bell"></i>
					<span>Automatiska varningar</span>
				</div>
			</div>
			<Link to="/bank">
				<button class="action-button b2b-cta">
					Läs mer om Bankpaketet <i class="fas fa-arrow-right"></i>
				</button>
			</Link>
		</div>
	</section>

	<section class="about-section">
		<h2>Vilka är vi?</h2>
		<p>
			Boanalys är ett AI-företag som banar vägen för nästa generation av
			analysverktyg för privatpersoner och företag. Läs mer om vår vision
			och vårt team.
		</p>
		<Link to="/about">
			<button class="action-button">Om Boanalys</button>
		</Link>
	</section>
</main>

<style>
	.hero {
		position: relative;
		color: #333;
		text-align: center;
		padding: 100px 20px 120px;
		margin-top: -20px;
	}

	.animated-background {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		overflow: hidden;
		z-index: 1;
		filter: blur(100px);
	}
	.shape {
		position: absolute;
		border-radius: 50%;
		opacity: 0.7;
	}
	.shape-1 {
		width: 400px;
		height: 400px;
		background-color: #f0a8a0;
		top: 10%;
		left: 20%;
		animation: move1 10s infinite alternate ease-in-out;
	}
	.shape-2 {
		width: 500px;
		height: 500px;
		background-color: #a3abbd;
		top: 40%;
		left: 50%;
		animation: move2 12s infinite alternate ease-in-out;
	}
	.shape-3 {
		width: 350px;
		height: 350px;
		background-color: #d6e4d7;
		bottom: 10%;
		right: 20%;
		animation: move3 11s infinite alternate ease-in-out;
	}

	.shape-4 {
		width: 450px;
		height: 450px;
		background-color: #a3d5f7;
		top: 60%;
		left: 10%;
		animation: move4 14s infinite alternate ease-in-out;
	}

	@keyframes move1 {
		from {
			transform: translate(0, 0) rotate(0deg);
		}
		to {
			transform: translate(100px, 50px) rotate(30deg);
		}
	}

	@keyframes move2 {
		from {
			transform: translate(0, 0) scale(1);
		}
		to {
			transform: translate(-80px, 60px) scale(1.2);
		}
	}

	@keyframes move3 {
		from {
			transform: translate(0, 0);
		}
		to {
			transform: translate(-50px, -100px);
		}
	}

	@keyframes move4 {
		from {
			transform: translate(0, 0) rotate(0deg) scale(1);
		}
		to {
			transform: translate(120px, -80px) rotate(-45deg) scale(1.1);
		}
	}

	.hero-content {
		position: relative;
		z-index: 5;
	}

	.hero-content h1 {
		font-size: 2.6rem;
		margin-bottom: 20px;
		line-height: 1.2;
		max-width: 800px;
		margin-left: auto;
		margin-right: auto;
	}

	.hero-content .sub-headline {
		font-size: 1.2rem;
		max-width: 700px;
		margin: 0 auto 20px;
		opacity: 0.9;
	}

	.trust-badges {
		display: flex;
		justify-content: center;
		gap: 30px;
		margin: 20px auto 30px;
		flex-wrap: wrap;
	}

	.trust-badge {
		font-size: 0.95rem;
		color: var(--hover);
		font-weight: 500;
		background: rgba(255, 255, 255, 0.8);
		padding: 8px 16px;
		border-radius: 20px;
		border: 1px solid rgba(236, 119, 106, 0.35);
	}

	.benefits-section {
		text-align: center;
		padding: 80px 20px;
	}

	.benefits-section h2 {
		font-size: 2.5rem;
		margin-bottom: 60px;
	}

	.benefits {
		display: flex;
		justify-content: space-around;
		max-width: 1200px;
		margin: 0 auto;
		gap: 40px;
		flex-wrap: wrap;
	}

	.benefit {
		flex: 1;
		min-width: 280px;
		max-width: 320px;
	}

	.benefit i {
		color: var(--hover);
	}

	.benefit h3 {
		font-size: 1.4rem;
		margin: 20px 0 10px;
		color: #333;
	}

	.benefit p {
		font-size: 1rem;
		line-height: 1.6;
		color: #555;
	}

	.how-it-works {
		text-align: center;
		padding: 80px 20px;
	}

	.how-it-works h2 {
		font-size: 2.5rem;
		margin-bottom: 60px;
	}

	.steps {
		display: flex;
		justify-content: space-around;
		max-width: 1000px;
		margin: 0 auto;
		gap: 30px;
		flex-wrap: wrap;
	}

	.step {
		flex: 1;
		min-width: 250px;
	}

	.step-icon {
		background-color: var(--hover);
		color: white;
		width: 70px;
		height: 70px;
		border-radius: 50%;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		margin-bottom: 20px;
		font-size: 1.8rem;
	}

	.step h3 {
		font-size: 1.4rem;
		margin-bottom: 10px;
	}

	.upload-section {
		text-align: center;
		margin-top: 60px;
	}

	.upload-content h2 {
		font-size: 2.5rem;
		margin-bottom: 20px;
	}

	.upload-content p {
		font-size: 1.1rem;
		max-width: 600px;
		margin: 0 auto 30px;
	}

	.about-map-section {
		padding: 60px 20px;
	}

	.about-map-content {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 60px;
		max-width: 1400px;
		margin: 0 auto;
		align-items: start;
	}

	.about-container {
		text-align: center;
		display: flex;
		flex-direction: column;
		justify-content: center;
		height: 100%;
	}

	.about-container h2 {
		font-size: 2.5rem;
		margin-bottom: 30px;
	}

	.about-container p {
		font-size: 1.1rem;
		max-width: 600px;
		margin: 0 auto 30px;
		line-height: 1.6;
	}

	.map-container {
		text-align: center;
		display: flex;
		align-items: center;
		justify-content: center;
		height: 100%;
	}

	.map-container :global(#map) {
		height: 500px;
		border-radius: 12px;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
	}

	.toplist-section {
		padding: 160px 20px 20px;
	}

	.toplist-container {
		max-width: 1400px;
		margin: 0 auto;
	}

	/* ─── B2B Bank section ─────────────────────────── */
	.b2b-section {
		padding: 80px 20px;
		text-align: center;
	}

	.b2b-content {
		max-width: 800px;
		margin: 0 auto;
	}

	.b2b-badge {
		display: inline-block;
		background: #fff0ed;
		border: 1px solid rgba(236, 119, 106, 0.4);
		padding: 6px 18px;
		border-radius: 20px;
		font-size: 0.82rem;
		font-weight: 600;
		letter-spacing: 0.07em;
		text-transform: uppercase;
		color: var(--hover);
		margin-bottom: 24px;
	}

	.b2b-section h2 {
		font-size: 2.2rem;
		margin-bottom: 18px;
		color: #222;
	}

	.b2b-section p {
		font-size: 1.05rem;
		line-height: 1.7;
		color: #555;
		max-width: 620px;
		margin: 0 auto 36px;
	}

	.b2b-features {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 12px 30px;
		margin-bottom: 36px;
	}

	.b2b-feature {
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: 0.93rem;
		color: #444;
	}

	.b2b-feature i {
		color: var(--hover);
		font-size: 0.95rem;
	}

	.b2b-cta {
		background-color: var(--hover);
		color: white;
		display: inline-flex;
		align-items: center;
		gap: 10px;
		padding: 14px 30px;
		font-size: 1rem;
	}

	.b2b-cta:hover {
		background-color: #e85a4a;
		color: white;
	}

	/* ─── About section ─────────────────────────────── */
	.about-section {
		text-align: center;
		padding: 80px 20px;
		max-width: 800px;
		margin: 0 auto;
	}

	.about-section h2 {
		font-size: 2.5rem;
		margin-bottom: 30px;
	}

	.about-section p {
		font-size: 1.1rem;
		max-width: 600px;
		margin: 0 auto 30px;
	}

	.button-group {
		display: flex;
		justify-content: center;
		gap: 15px;
		margin-top: 20px;
	}

	@media (max-width: 768px) {
		.hero-content h1 {
			font-size: 2.2rem;
		}
		.trust-badges {
			flex-direction: column;
			gap: 15px;
			align-items: center;
		}
		.about-map-content {
			grid-template-columns: 1fr;
			gap: 40px;
		}
		.about-container {
			padding: 20px 10px;
		}
		.benefits {
			flex-direction: column;
			align-items: center;
		}

		.upload-content p {
			max-width: 90%;
		}
	}
</style>
